import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, remove, get } from 'firebase/database';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
    FaSearch, 
    FaEdit, 
    FaTrashAlt, 
    FaCopy,
    FaFileExcel,
    FaPrint,
    FaFilePdf,
    FaSort
} from 'react-icons/fa';
import * as XLSX from 'xlsx';

const AuditWorkList = () => {
    const [works, setWorks] = useState([]);
    const [lotteryResults, setLotteryResults] = useState({});
    const [availableDates, setAvailableDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    // Determine title based on path
    const isLotteryCompleted = location.pathname.includes('/lottery-completed');
    const pageTitle = "कामाची यादी";

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        // 1. Fetch available lottery dates from generatework
        const generateWorkDatesRef = ref(db, 'ekamwatapzp/generatework');
        onValue(generateWorkDatesRef, (snapshot) => {
            if (snapshot.exists()) {
                const dates = Object.keys(snapshot.val());
                setAvailableDates(dates.sort((a, b) => {
                    const dateA = new Date(a.split('-').reverse().join('-'));
                    const dateB = new Date(b.split('-').reverse().join('-'));
                    return dateB - dateA;
                }));
            }
        });

        // 2. Fetch Lottery Results for winner lookup
        const resultsRef = ref(db, 'ekamwatapzp/LotteryResults');
        onValue(resultsRef, (snapshot) => {
            if (snapshot.exists()) {
                setLotteryResults(snapshot.val());
            }
        });

        // 3. Initial Load for non-lottery-completed view
        if (!isLotteryCompleted) {
            const worksRef = ref(db, 'ekamwatapzp/WorkNames');
            onValue(worksRef, (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    let flattenedWorks = [];
                    
                    Object.entries(data).forEach(([key, val]) => {
                        if (val && typeof val === 'object') {
                            if (val.schemeName) {
                                flattenedWorks.push({ id: key, meetingNumber: val.meetingNumber || val.category || 'General', ...val });
                            } else {
                                const meetingNum = key;
                                Object.entries(val).forEach(([id, value]) => {
                                    if (value && typeof value === 'object') {
                                        flattenedWorks.push({ id, meetingNumber: meetingNum, ...value });
                                    }
                                });
                            }
                        }
                    });
                    setWorks(flattenedWorks.reverse());
                } else {
                    setWorks([]);
                }
                setLoading(false);
            });
        } else {
            setWorks([]);
            setLoading(false);
        }

        return () => unsubscribe();
    }, [navigate, isLotteryCompleted]);

    const handleDateSearch = async () => {
        if (!selectedDate) {
            toast.warning("कृपया लॉटरीची तारीख निवडा");
            return;
        }

        setLoading(true);
        const dateRef = ref(db, `ekamwatapzp/generatework/${selectedDate}`);
        try {
            const snapshot = await get(dateRef);
            if (snapshot.exists()) {
                const data = snapshot.val();
                let flattenedWorks = [];
                
                Object.entries(data).forEach(([key, val]) => {
                    if (val && typeof val === 'object') {
                        if (val.schemeName) {
                            flattenedWorks.push({ id: key, meetingNumber: val.meetingNumber || val.category || 'General', ...val });
                        } else {
                            const meetingNum = key;
                            Object.entries(val).forEach(([id, value]) => {
                                if (value && typeof value === 'object') {
                                    flattenedWorks.push({ id, meetingNumber: meetingNum, ...value });
                                }
                            });
                        }
                    }
                });
                setWorks(flattenedWorks);
            } else {
                setWorks([]);
                toast.info("या तारखेसाठी कोणतीही कामे आढळली नाहीत.");
            }
        } catch (error) {
            toast.error("डेटा फेच करताना त्रुटी: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = works.filter(item => {
        const term = searchTerm.toLowerCase();
        return (
            (item.workName || '').toLowerCase().includes(term) ||
            (item.taluka || '').toLowerCase().includes(term) ||
            (item.schemeName || '').toLowerCase().includes(term) ||
            (item.workNumber || '').toLowerCase().includes(term) ||
            (item.id || '').toLowerCase().includes(term)
        );
    });

    const getWinnerType = (meetingNumber, workId) => {
        const dateKey = isLotteryCompleted ? selectedDate : (meetingNumber || "General");
        const meetingPath = dateKey.toString().replace(/[.#$[\]]/g, "_");
        const workPath = workId ? workId.toString().replace(/[.#$[\]]/g, "_") : "";
        
        if (lotteryResults[meetingPath] && lotteryResults[meetingPath][workPath]) {
            const winnerId = lotteryResults[meetingPath][workPath];
            if (typeof winnerId === 'string') {
                if (winnerId.startsWith('Engineer_')) return "सु.बे. अभियंता";
                if (winnerId.startsWith('Society_')) return "मजूर संस्था";
            }
        }
        return "-";
    };

    const downloadExcel = () => {
        const excelData = filteredData.map((item, index) => ({
            'अ.क्र.': index + 1,
            'वर्क नंबर': item.workNumber || item['वर्क नंबर'] || item.id,
            'तालुका': item.taluka,
            'योजनेचे नाव': item.schemeName,
            ...(isLotteryCompleted ? { 'ट्रान्स्फर वर्क': item.sharedwork || getWinnerType(item.meetingNumber, item.id) } : {}),
            'कामाचे नाव': item.workName,
            'निविदा रक्कम': item.tenderAmount,
            'नोंदणी दिनांक': item.allocatedAt ? new Date(item.allocatedAt).toLocaleDateString() : (item.date || '-'),
            'कामाची स्थिती': item.status || 'Pending'
        }));

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Audit_Work_List");
        XLSX.writeFile(wb, `Audit_Work_List_${selectedDate || 'All'}.xlsx`);
        toast.info("एक्सेल फाईल डाउनलोड होत आहे...");
    };

    const copyToClipboard = () => {
        const headers = ['अ.क्र.', 'वर्क नंबर', 'तालुका', 'योजनेचे नाव', 'कामाचे नाव', 'रक्कम', 'स्थिती'];
        const rows = filteredData.map((item, index) => [
            index + 1,
            item.workNumber || item.id,
            item.taluka,
            item.schemeName,
            item.workName,
            item.tenderAmount,
            item.status || 'Pending'
        ]);

        const content = [headers, ...rows].map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(content);
        toast.success("माहिती कॉपी झाली!");
    };

    const handleDelete = async (work) => {
        const workPath = !work.meetingNumber || work.meetingNumber === 'General'
            ? `ekamwatapzp/WorkNames/${work.id}` 
            : `ekamwatapzp/WorkNames/${work.meetingNumber}/${work.id}`;
            
        if (window.confirm("तुम्ही हे काम हटवू इच्छिता का?")) {
            try {
                await remove(ref(db, workPath));
                toast.success("यशस्वीरीत्या हटवले!");
            } catch (error) {
                toast.error("त्रुटी: " + error.message);
            }
        }
    };

    if (loading && works.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1a1a4d]"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 no-print">
                <h1 className="text-2xl font-bold text-gray-800">{pageTitle}</h1>
                <div className="text-sm font-medium text-gray-500">
                    सांगली जिल्हा परिषद  /  <span className="text-gray-400 font-bold">{pageTitle}</span>
                </div>
            </div>

            {isLotteryCompleted && (
                <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 mb-8 no-print">
                    <div className="flex flex-col gap-3">
                        <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                            लॉटरीची तारीख निवडा <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-col md:flex-row gap-4 max-w-2xl">
                            <div className="relative flex-grow">
                                <select 
                                    className="w-full bg-white border border-gray-300 rounded-lg py-2 px-4 focus:ring-1 focus:ring-blue-500 outline-none transition font-medium appearance-none h-11"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                >
                                    <option value="">लॉटरीची तारीख निवडा</option>
                                    {availableDates.map(date => (
                                        <option key={date} value={date}>{date}</option>
                                    ))}
                                </select>
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 font-black">⌵</div>
                            </div>
                            <button 
                                onClick={handleDateSearch}
                                className="bg-[#4e73df] hover:bg-blue-700 text-white px-8 h-11 rounded-lg font-bold shadow transition transform active:scale-95 flex items-center justify-center"
                            >
                                Search
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-6 border-b border-gray-200 no-print">
                    <div className="flex items-center">
                        <div className="inline-flex rounded-md shadow-sm" role="group">
                            <button onClick={copyToClipboard} className="px-6 py-2 bg-[#6c757d] text-white rounded-l text-sm font-bold border-r border-[#5a6268] hover:bg-[#5a6268] transition flex items-center gap-2">Copy</button>
                            <button onClick={downloadExcel} className="px-6 py-2 bg-[#6c757d] text-white text-sm font-bold border-r border-[#5a6268] hover:bg-[#5a6268] transition flex items-center gap-2">Excel</button>
                            <button onClick={() => window.print()} className="px-6 py-2 bg-[#6c757d] text-white rounded-r text-sm font-bold hover:bg-[#5a6268] transition flex items-center gap-2">Print</button>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <span className="text-sm font-bold text-gray-500">Search:</span>
                        <div className="relative">
                            <input 
                                type="text" 
                                className="w-64 pl-4 pr-10 py-2 border border-gray-300 rounded text-sm font-medium outline-none h-10"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#f8f9fa] border-b border-gray-300">
                            <tr>
                                <th className="px-4 py-4 text-[11px] font-black text-gray-600 uppercase border-r text-center whitespace-nowrap">क्र. <FaSort className="inline text-[9px] text-gray-300" /></th>
                                <th className="px-4 py-4 text-[11px] font-black text-gray-600 uppercase border-r text-center whitespace-nowrap">वर्क नंबर <FaSort className="inline text-[9px] text-gray-300" /></th>
                                <th className="px-4 py-4 text-[11px] font-black text-gray-600 uppercase border-r text-center whitespace-nowrap">तालुका <FaSort className="inline text-[9px] text-gray-300" /></th>
                                <th className="px-4 py-4 text-[11px] font-black text-gray-600 uppercase border-r text-center whitespace-nowrap">योजनेचे नाव <FaSort className="inline text-[9px] text-gray-300" /></th>
                                <th className="px-4 py-4 text-[11px] font-black text-gray-600 uppercase border-r text-center whitespace-nowrap">ट्रान्स्फर वर्क <FaSort className="inline text-[9px] text-gray-300" /></th>
                                <th className="px-4 py-4 text-[11px] font-black text-gray-600 uppercase border-r min-w-[300px] whitespace-nowrap">कामाचे नाव <FaSort className="inline text-[9px] text-gray-300" /></th>
                                <th className="px-4 py-4 text-[11px] font-black text-gray-600 uppercase border-r text-center whitespace-nowrap">नोंदणी दिनांक <FaSort className="inline text-[9px] text-gray-300" /></th>
                                <th className="px-4 py-4 text-[11px] font-black text-gray-600 uppercase border-r text-center whitespace-nowrap">जावक नंबर / प्रमा नंबर / दिनांक <FaSort className="inline text-[9px] text-gray-300" /></th>
                                <th className="px-4 py-4 text-[11px] font-black text-gray-600 uppercase border-r text-center whitespace-nowrap">कामाची स्थिती <FaSort className="inline text-[9px] text-gray-300" /></th>
                                <th className="px-4 py-4 text-[11px] font-black text-gray-600 uppercase border-r text-center whitespace-nowrap">Eng. रिमार्क <FaSort className="inline text-[9px] text-gray-300" /></th>
                                <th className="px-4 py-4 text-[11px] font-black text-gray-600 uppercase border-r text-center whitespace-nowrap">PA रिमार्क <FaSort className="inline text-[9px] text-gray-300" /></th>
                                <th className="px-4 py-4 text-[11px] font-black text-gray-600 uppercase text-center no-print whitespace-nowrap">Action <FaSort className="inline text-[9px] text-gray-300" /></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredData.map((item, index) => (
                                <tr key={item.id} className="transition-all odd:bg-white even:bg-[#f2f2f2]">
                                    <td className="px-4 py-5 text-[13px] font-bold text-gray-700 border-r text-center">{index + 1}</td>
                                    <td className="px-4 py-5 text-[13px] font-bold text-gray-800 border-r text-center">{item.workNumber || item['वर्क नंबर'] || item.id.substring(0, 8)}</td>
                                    <td className="px-4 py-5 text-[13px] font-bold text-gray-700 border-r text-center">{item.taluka}</td>
                                    <td className="px-4 py-5 text-[13px] font-medium text-gray-600 border-r text-center">{item.schemeName}</td>
                                    <td className="px-4 py-5 text-[13px] font-bold text-gray-700 border-r text-center">{item.sharedwork || getWinnerType(item.meetingNumber, item.id)}</td>
                                    <td className="px-4 py-5 border-r min-w-[300px]">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="font-bold text-gray-800 text-[13px] leading-snug">{item.workName}</span>
                                            <span className="text-[11px] font-bold text-gray-600 italic">Rs- {item.tenderAmount} /-</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-5 text-[13px] font-bold text-gray-600 border-r text-center whitespace-nowrap">
                                        {item.allocatedAt ? new Date(item.allocatedAt).toISOString().split('T')[0] : (item.date || '-')}
                                    </td>
                                    <td className="px-4 py-5 text-[12px] font-bold text-gray-600 border-r text-center italic">
                                        <div className="flex flex-col">
                                            <span>{item.inwardNo || '-'}</span>
                                            <span>{item.inwardDate || ''}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-5 text-center border-r">
                                        <span className="font-bold text-[13px] text-green-600">
                                            {item.status === 'Approved' ? 'Approved' : (item.status === 'lottery_completed' ? 'Approved' : (item.status || 'Pending'))}
                                        </span>
                                    </td>
                                    <td className="px-4 py-5 text-[13px] font-bold text-gray-500 border-r text-center">-</td>
                                    <td className="px-4 py-5 text-[13px] font-bold text-gray-500 border-r text-center">-</td>
                                    <td className="px-4 py-5 font-bold no-print">
                                        <div className="flex items-center justify-center gap-3">
                                            <FaEdit 
                                                className="text-gray-400 cursor-pointer hover:text-blue-600 transition text-[15px]" 
                                                title="Edit" 
                                                onClick={() => {
                                                    const meeting = item.meetingNumber || 'General';
                                                    const url = isLotteryCompleted 
                                                        ? `/audit/work-info/edit/${meeting}/${item.id}?date=${selectedDate}`
                                                        : `/audit/work-info/edit/${meeting}/${item.id}`;
                                                    navigate(url);
                                                }}
                                            />
                                            <FaFilePdf 
                                                className="text-gray-400 cursor-pointer hover:text-red-500 transition text-[15px]" 
                                                title="View PDF" 
                                                onClick={() => {
                                                    const meeting = item.meetingNumber || 'General';
                                                    const url = isLotteryCompleted 
                                                        ? `/audit/work-info/details/${meeting}/${item.id}?date=${selectedDate}`
                                                        : `/audit/work-info/details/${meeting}/${item.id}`;
                                                    navigate(url);
                                                }}
                                            />
                                            <FaTrashAlt 
                                                className="text-gray-400 cursor-pointer hover:text-red-600 transition text-[15px]" 
                                                title="Delete" 
                                                onClick={() => handleDelete(item)}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    {filteredData.length === 0 && (
                        <div className="py-20 text-center flex flex-col items-center gap-4 bg-white">
                            <FaSearch className="text-gray-200 text-5xl" />
                            <p className="text-gray-400 font-bold">कोणतेही काम आढळले नाही.</p>
                        </div>
                    )}
                </div>
            </div>
            
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; }
                    .p-4, .p-8 { padding: 0 !important; }
                    .shadow-lg { box-shadow: none !important; }
                    .border { border: 1px solid #ddd !important; }
                    table { border: 1px solid #ccc !important; width: 100% !important; }
                    th, td { border: 1px solid #ddd !important; padding: 10px !important; }
                }
            `}</style>
        </div>
    );
};

export default AuditWorkList;
